/**
 * Visual-only Google Pay button placeholder.
 *
 * Shows ONLY the official "G Pay" lockup (four-colour G + white "Pay"
 * wordmark, white-on-black) — no "Buy with" prefix — centred and enlarged
 * to fill the button. The G + "Pay" glyphs are the exact official Google
 * vectors (same source as `src/assets/icons/devices/google.svg`).
 *
 * The lockup lives inside one inline SVG (no `<img src>` network hop) so it
 * paints from t=0. Brand text ("Pay") is never user-translatable per Google's
 * guidelines.
 *
 * Pure visual — `aria-hidden`, no event handlers. The parent shell's loader
 * overlay blocks interaction; the real Stripe-rendered button (mounted
 * underneath) takes over once ECE reports ready.
 *
 * Source: https://developers.google.com/pay/api/web/guides/brand-guidelines
 */
export const GooglePayButtonPlaceholder = (): JSX.Element => (
  <div
    aria-hidden="true"
    className="wallet-visual-button w-full rounded-lg bg-black"
    data-testid="wallet-placeholder-google"
  >
    {/* viewBox tightly bounds the G + "Pay" lockup (native coords ~0.6–32.4 x,
        ~0.8–13.2 y) so it scales up large and centres in the button. */}
    <svg
      width="70"
      height="30"
      viewBox="0 0 33 14"
      role="img"
      aria-label="Google Pay"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Official Google "G Pay" lockup (four-colour G + white "Pay"), the
          exact vectors from assets/icons/devices/google.svg. */}
      <g>
        <path
          d="M10.8087 6.09573C10.8087 5.71844 10.7747 5.35741 10.7116 5.01025H5.82812V6.9992L8.6406 6.9998C8.52653 7.66157 8.15942 8.22571 7.59692 8.6018V9.8922H9.27106C10.2486 8.99356 10.8087 7.66519 10.8087 6.09573Z"
          fill="#4285F4"
        />
        <path
          d="M7.59738 8.60267C7.13136 8.91487 6.53124 9.09749 5.82919 9.09749C4.47301 9.09749 3.32253 8.18981 2.91052 6.96631H1.18359V8.29709C2.03917 9.98348 3.79765 11.1407 5.82919 11.1407C7.2333 11.1407 8.4129 10.682 9.27151 9.89247L7.59738 8.60267Z"
          fill="#34A853"
        />
        <path
          d="M2.74795 5.97829C2.74795 5.63475 2.8056 5.30266 2.91057 4.99045V3.65967H1.18365C0.829886 4.357 0.630859 5.14414 0.630859 5.97829C0.630859 6.81244 0.830493 7.59958 1.18365 8.29692L2.91057 6.96613C2.8056 6.65393 2.74795 6.32184 2.74795 5.97829Z"
          fill="#FABB05"
        />
        <path
          d="M5.82919 2.85959C6.59556 2.85959 7.28184 3.12177 7.82371 3.63407L9.30731 2.16165C8.40623 1.32811 7.23148 0.816406 5.82919 0.816406C3.79826 0.816406 2.03917 1.97361 1.18359 3.65999L2.91052 4.99077C3.32253 3.76727 4.47301 2.85959 5.82919 2.85959Z"
          fill="#E94235"
        />
        <path
          d="M15.4443 6.83172V10.4781H14.2793V1.47363H17.3679C18.1506 1.47363 18.8181 1.7328 19.3642 2.25113C19.9225 2.76946 20.2016 3.40231 20.2016 4.14966C20.2016 4.91511 19.9225 5.54795 19.3642 6.06025C18.8242 6.57256 18.1567 6.8257 17.3679 6.8257H15.4443V6.83172ZM15.4443 2.58262V5.72274H17.3921C17.8533 5.72274 18.2416 5.56603 18.545 5.25865C18.8545 4.95127 19.0123 4.57759 19.0123 4.15569C19.0123 3.73982 18.8545 3.37217 18.545 3.06479C18.2416 2.74535 17.8594 2.58865 17.3921 2.58865H15.4443V2.58262Z"
          fill="#fff"
        />
        <path
          d="M23.2473 4.11377C24.109 4.11377 24.7886 4.3428 25.2861 4.80086C25.7837 5.25892 26.0325 5.88574 26.0325 6.68131V10.4784H24.9221V9.62254H24.8735C24.3941 10.3277 23.7509 10.6773 22.95 10.6773C22.2643 10.6773 21.6939 10.4784 21.2328 10.0746C20.7716 9.67075 20.541 9.1705 20.541 8.56779C20.541 7.92892 20.7837 7.42265 21.2692 7.04896C21.7546 6.66926 22.4039 6.48242 23.2109 6.48242C23.9026 6.48242 24.473 6.60899 24.916 6.86213V6.59693C24.916 6.19312 24.7582 5.8556 24.4366 5.57233C24.115 5.28905 23.7388 5.15043 23.308 5.15043C22.6587 5.15043 22.1429 5.42165 21.7667 5.97011L20.7413 5.33124C21.3056 4.51758 22.1429 4.11377 23.2473 4.11377ZM21.7425 8.58588C21.7425 8.88723 21.8699 9.14037 22.1308 9.33926C22.3857 9.53816 22.6891 9.64062 23.0349 9.64062C23.5264 9.64062 23.9633 9.4598 24.3456 9.09818C24.7279 8.73655 24.9221 8.31466 24.9221 7.82646C24.558 7.54319 24.0543 7.39854 23.4051 7.39854C22.9318 7.39854 22.5374 7.51305 22.2218 7.73606C21.9002 7.97111 21.7425 8.25438 21.7425 8.58588Z"
          fill="#fff"
        />
        <path
          d="M32.3674 4.31201L28.484 13.1839H27.2825L28.7267 10.0799L26.166 4.31201H27.4342L29.2789 8.7359H29.3031L31.0992 4.31201H32.3674Z"
          fill="#fff"
        />
      </g>
    </svg>
  </div>
);
